import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Col, Form, Row, Select, Space, Button, DatePicker, InputNumber, Input } from 'antd';
import { getById } from '../../../../apis/DefinicionAutorizacionApi';
import { getEmpleadosActivos } from '../../../../apis/OdooApi';
import { getAll as getMotivos } from '../../../../apis/MotivosApi';
import TIPO_AUTORIZACION_OPTIONS from '../../../../constants/tipoAutorizacion';
import moment from 'moment';
import _ from 'lodash';

const { Option } = Select;
const { TextArea } = Input;

const FormBody = forwardRef(function FormBody({ onOk, onCancel, form, object, disabledForm, period }, ref) {
    const [ID, setID] = useState(null);
    const [disabled, setDisabled] = useState(disabledForm ?? false);
    const [initialValues, setInitialValues] = useState(null);
    const [empleados, setEmpleados] = useState([]);
    const [motivos, setMotivos] = useState([]);
    const [periodObject, setPeriodObject] = useState(period);

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                await getCatalogs();
            })();
        }
        return () => effectRan.current = true;
    }, []);

    useEffect(() => {
        console.log('[DEBUG] FormBody - period changed', period);
        if (period) {
            setPeriodObject(period);
        }
    }, [period]);

    useEffect(() => {
        (async () => {
            if (object?.id > 0) {
                setID(object.id);
                await refreshData(object.id);
            }
        })();
    }, [object]);

    useEffect(() => {
        (async () => {
            console.log('[DEBUG] FormBody - disabledForm changed', disabledForm);
            setDisabled(disabledForm)
        })();
    }, [disabledForm]);

    const refreshData = async (id) => {
        const response = await getById(id);
        if (response.ok) {
            let item = response.payload;
            // Convertir la fecha al formato moment
            const formattedItem = {
                empleado_id: item.empleado?.id || null,
                fecha: item.fecha ? moment(item.fecha, 'YYYY-MM-DD') : null,
                tipo_autorizacion: item.tipo_autorizacion,
                hora: item.valor || null,
                motivo_id: item.motivo?.id || null,
                comentarios: item.comentarios || ''
            };
            setInitialValues(formattedItem);
            form.setFieldsValue(formattedItem);
        }
    }

    const getCatalogs = async () => {
        // Obtener empleados
        const responseEmpleados = await getEmpleadosActivos({ nomina_need_timesheets: true });
        if (responseEmpleados.ok) {
            let items = responseEmpleados.payload;
            setEmpleados(items);
        }

        // Obtener motivos
        const responseMotivos = await getMotivos();
        if (responseMotivos.ok) {
            let items = responseMotivos.payload.map(item => ({
                value: item.id,
                text: item.clave,
                id: item.id
            }));
            setMotivos(items);
        }
    }

    const onReset = () => {
        form.resetFields();
    };

    const onCancelBtn = async () => {
        if (onCancel) {
            await onCancel();
        }
    }

    const onFinish = async () => {
        await onOk();
    }

    return (
        <>
            <Form disabled={disabled}
                layout="vertical"
                form={form}
                requiredMark
                autoComplete="off"
                initialValues={{
                    empleado_id: null,
                    fecha: null,
                    tipo_autorizacion: null,
                    hora: null,
                    motivo_id: null,
                    comentarios: ''
                }}
                onFinish={onFinish}>
                <Row gutter={16}>
                    <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                        <Form.Item
                            name="empleado_id"
                            label="Empleado"
                            rules={[
                                {
                                    required: true,
                                    message: 'Selecciona un empleado',
                                },
                            ]}
                        >
                            <Select
                                placeholder="Por favor selecciona un empleado"
                                showSearch
                                optionFilterProp="children"
                            >
                                {
                                    empleados.map((item) => <Option key={item.value} value={item.value}>{item.text}</Option>)
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                        <Form.Item
                            name="fecha"
                            label="Fecha"
                            rules={[
                                {
                                    required: true,
                                    message: 'Selecciona una fecha',
                                },
                            ]}
                        >
                            <DatePicker
                                placeholder="Selecciona una fecha"
                                format="DD/MM/YYYY"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                        <Form.Item
                            name="tipo_autorizacion"
                            label="Tipo Autorización"
                            rules={[
                                {
                                    required: true,
                                    message: 'Selecciona un tipo de autorización',
                                },
                            ]}
                        >
                            <Select placeholder="Por favor selecciona un tipo de autorización">
                                {
                                    TIPO_AUTORIZACION_OPTIONS.map((item) => <Option key={item.value} value={item.value}>{item.text}</Option>)
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                        <Form.Item
                            name="hora"
                            label="Hora"
                            rules={[
                                {
                                    required: false,
                                    message: 'Ingresa las horas',
                                },
                            ]}
                        >
                            <InputNumber
                                placeholder="Ingresa las horas"
                                style={{ width: '100%' }}
                                min={0}
                                max={24}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                        <Form.Item
                            name="motivo_id"
                            label="Motivo"
                            rules={[
                                {
                                    required: false,
                                    message: 'Selecciona un motivo',
                                },
                            ]}
                        >
                            <Select
                                placeholder="Por favor selecciona un motivo"
                                showSearch
                                optionFilterProp="children"
                                allowClear
                            >
                                {
                                    motivos.map((item) => <Option key={item.value} value={item.value}>{item.text}</Option>)
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 24 }}>
                        <Form.Item
                            name="comentarios"
                            label="Comentarios"
                            rules={[
                                {
                                    required: false,
                                    message: 'Ingresa comentarios',
                                },
                            ]}
                        >
                            <TextArea
                                placeholder="Ingresa comentarios adicionales"
                                rows={4}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    <Space>
                        {
                            ID &&
                            <Button type="default" onClick={onCancelBtn}>
                                Cancelar
                            </Button>
                        }

                        <Button type="primary" htmlType="submit" disabled={!periodObject}>
                            Guardar
                        </Button>
                        <Button htmlType="button" onClick={onReset}>
                            Limpiar
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </>
    );
});

export default FormBody;